import { DashboardOutlined } from '@ant-design/icons';
import flow from 'lodash/fp/flow';
import get from 'lodash/fp/get';

import { useStores } from '@/hooks/use-stores';
import { DeviceType } from '@/constants/deviceTypeEnum';
import { IconWrapper } from '@/comp/common/icons/IconWrapper';
import FlowIcon from '@/comp/common/icons/FlowIcon';
import PressureIcon from '@/comp/common/icons/PressureIcon';
import DewPointIcon from '@/comp/common/icons/DewPointIcon';
import AirTemperatureIcon from '@/comp/common/icons/AirTemperatureIcon';
import { CombinationIconWrapper } from '@/comp/common/icons/CombinationIconWrapper';
import PowerKpiIcon from '@/comp/common/icons/PowerKpiIcon';
import WaterPressureIcon from '@/comp/common/icons/WaterPressureIcon';

import { Entity } from '../../../NodeWidget.types';

export const useInitialValues = (entity: Entity) => {
  const { devicesStore } = useStores();
  const controllerId = entity?.controllerId ?? devicesStore.getDeviceControllerId(entity?.deviceId);

  return {
    name: entity?.name,
    deviceType: entity?.deviceType,
    deviceModelId: entity?.deviceModelId,
    controllerId,
  };
};

export const getOnDeviceSensorOptions = (t, token) => [
  {
    value: DeviceType.FlowSensor,
    label: (
      <div className="device-type-label">
        <IconWrapper>
          <FlowIcon color={token.colorIcon} />
        </IconWrapper>
        {t('Flow')}
      </div>
    ),
  },
  {
    value: DeviceType.PowerSensor,
    label: (
      <div className="device-type-label">
        <PowerKpiIcon color={token.colorIcon} />
        {t('Power')}
      </div>
    ),
  },
  {
    value: DeviceType.PressureSensor,
    label: (
      <div className="device-type-label">
        <IconWrapper>
          <PressureIcon fillColor={token.colorIcon} />
        </IconWrapper>
        {t('Pressure')}
      </div>
    ),
  },
  {
    value: DeviceType.DewpointSensor,
    label: (
      <div className="device-type-label">
        <IconWrapper>
          <DewPointIcon color={token.colorIcon} />
        </IconWrapper>
        {t('Dew Point')}
      </div>
    ),
  },
  {
    value: DeviceType.AirTemperatureSensor,
    label: (
      <div className="device-type-label">
        <IconWrapper>
          <AirTemperatureIcon fillColor={token.colorIcon} innerColor="transparent" />
        </IconWrapper>
        {t('Air Temp')}
      </div>
    ),
  },
  {
    value: DeviceType.FlowAndPressureSensor,
    label: (
      <div className="device-type-label">
        <CombinationIconWrapper
          icon1={<FlowIcon color={token.colorIcon} />}
          icon2={<PressureIcon fillColor={token.colorIcon} />}
        />
        {t('Flow & Pressure')}
      </div>
    ),
  },
  {
    value: DeviceType.DewpointAndPressureSensor,
    label: (
      <div className="device-type-label">
        <CombinationIconWrapper
          icon1={<DewPointIcon color={token.colorIcon} />}
          icon2={<PressureIcon fillColor={token.colorIcon} />}
        />
        {t('Dew Point & Pressure')}
      </div>
    ),
  },
  {
    value: DeviceType.WaterPressureSensor,
    label: (
      <div className="device-type-label">
        <IconWrapper>
          <WaterPressureIcon fillColor={token.colorIcon} innerColor="transparent" />
        </IconWrapper>
        {t('Water Pressure')}
      </div>
    ),
  },
  {
    value: DeviceType.WaterFlowSensor,
    label: (
      <div className="device-type-label">
        <DashboardOutlined className="icon" />
        {t('Water Flow')}
      </div>
    ),
  },
  {
    value: DeviceType.WaterTemperatureSensor,
    label: (
      <div className="device-type-label">
        <DashboardOutlined className="icon" />
        {t('Water Temperature')}
      </div>
    ),
  },
  {
    value: DeviceType.AmbientHumiditySensor,
    label: (
      <div className="device-type-label">
        <DashboardOutlined className="icon" />
        {t('Ambient Humidity')}
      </div>
    ),
  },
  {
    value: DeviceType.AmbientTemperatureSensor,
    label: (
      <div className="device-type-label">
        <DashboardOutlined className="icon" />
        {t('Ambient Temperature')}
      </div>
    ),
  },
  {
    value: DeviceType.AmbientHumidityAndTemperatureSensor,
    label: (
      <div className="device-type-label">
        <DashboardOutlined className="icon" />
        {t('Ambient Humidity & Temperature')}
      </div>
    ),
  },
];

const getHandler = get('handler');
const getText = get('text');

export const getConfirmButtonProps = (isAddingSensor, isEditing, onAddClick, onSaveClick, onEditClick) => {
  if (isAddingSensor) return { handler: onAddClick, text: 'addSensor' };
  if (isEditing) return { handler: onSaveClick, text: 'save' };
  return { handler: onEditClick, text: 'edit' };
};

export const getConfirmButtonHandler = flow(getConfirmButtonProps, getHandler);
export const getConfirmButtonText = flow(getConfirmButtonProps, getText);

export const getCancelButtonProps = (isEditing, onCancelClick, onDeleteClick) => {
  if (isEditing) return { handler: onCancelClick, text: 'cancel' };
  return { handler: onDeleteClick, text: 'delete' };
};

export const getCancelButtonHandler = flow(getCancelButtonProps, getHandler);
export const getCancelButtonText = flow(getCancelButtonProps, getText);
